/**
 * Board ↔ Google Drive UI for the whiteboard app: the folder picker (create
 * dialog + "Choose folder"), the Share / access panel (Drive status, who can
 * open it, the optional view-only share link) and the dashboard prompt for
 * boards that aren't in Drive yet. Self-contained styles (boardDrive.css), so
 * it doesn't depend on Excalidraw's chrome.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { BoardAccessRow, BoardDriveApi, BoardDriveStatus, BoardRole, DriveCrumb, DriveFolder } from '../boardDrive';
import { roleLabel } from '../boardDrive';
import './boardDrive.css';

// ---- modal ----------------------------------------------------------------------------

export function DriveModal({ title, onClose, children, testId }: { title: string; onClose(): void; children: ReactNode; testId?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return createPortal(
    <div className="bd-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bd-modal" role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
        <div className="bd-head">
          <h2>{title}</h2>
          <button type="button" className="bd-icon" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="bd-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ---- folder picker --------------------------------------------------------------------

interface Crumb {
  id: string | null;
  name: string;
}

/** What the picker needs from an API: boards pass boardDriveApi; the PDF library passes its own `folders`. */
export interface FolderSource {
  folders(opts: { parent?: string | null; q?: string }): Promise<{ folders: DriveFolder[]; recent: DriveFolder[]; shortcuts?: DriveFolder[] }>;
  /** A folder's real path (top of the picker first, the folder last), for opening a search hit or shortcut. */
  trail?(folderId: string): Promise<{ trail: DriveCrumb[] }>;
}

/**
 * Browse / search the user's Drive folders. `onPick` gets the folder to use
 * (the current folder via "Use this folder", or a row's "Choose"). At the
 * root, `shortcuts` from the source (e.g. the eval folders, the last folder
 * used) are pinned above Recent. Search rows say where each folder lives;
 * opening a search hit (or a shortcut / recent folder) clears the search and
 * shows the folder's real path in the breadcrumb, so you can drill down or
 * step back up from there.
 */
export function FolderPicker({ api, onPick, busy }: { api: FolderSource; onPick(f: DriveFolder & { pathLabel: string }): void; busy?: boolean }) {
  const [trail, setTrail] = useState<Crumb[]>([{ id: null, name: 'My Drive & shared drives' }]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<DriveFolder[] | null>(null);
  const [recent, setRecent] = useState<DriveFolder[]>([]);
  const [shortcuts, setShortcuts] = useState<DriveFolder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const here = trail[trail.length - 1];
  const seq = useRef(0);
  const trailSeq = useRef(0);
  const [trailError, setTrailError] = useState<string | null>(null);

  /** Open a folder found by search (or pinned): its real ancestors become the breadcrumb. */
  const reveal = (f: DriveFolder) => {
    const n = ++trailSeq.current;
    setQ('');
    setTrailError(null);
    setTrail([trail[0], { id: f.id, name: f.name }]);
    if (!api.trail) return;
    api
      .trail(f.id)
      .then((r) => {
        if (n === trailSeq.current && r.trail.length) setTrail([trail[0], ...r.trail.map((c) => ({ id: c.id, name: c.name }))]);
      })
      .catch((e: Error) => n === trailSeq.current && setTrailError(`Couldn't load the folder's path: ${e.message}`));
  };
  const openChild = (f: DriveFolder) => {
    trailSeq.current++;
    setTrailError(null);
    setTrail([...trail, { id: f.id, name: f.name }]);
  };
  const goUp = (i: number) => {
    trailSeq.current++;
    setTrail(trail.slice(0, i + 1));
  };

  const load = useCallback(() => {
    const n = ++seq.current;
    setRows(null);
    setError(null);
    const query = q.trim();
    api
      .folders(query ? { q: query } : { parent: here.id })
      .then((r) => {
        if (n !== seq.current) return;
        setRows(r.folders);
        if (!here.id && !query) {
          setRecent(r.recent);
          setShortcuts(r.shortcuts ?? []);
        }
      })
      .catch((e: Error) => n === seq.current && setError(e.message));
  }, [api, here.id, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const pathLabel = (extra?: string) => [...trail.slice(1).map((c) => c.name), ...(extra ? [extra] : [])].join(' / ');
  const searching = q.trim().length > 0;

  return (
    <div className="bd-picker" data-testid="folder-picker">
      <input className="bd-input" type="search" placeholder="Search Drive folders" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search Drive folders" />
      {!searching && !here.id && shortcuts.length > 0 && (
        <div className="bd-section" data-testid="folder-shortcuts">
          <div className="bd-label">Shortcuts</div>
          {shortcuts.map((f) => (
            <FolderRow key={`s-${f.id}`} f={f} sub={f.path} onOpen={() => reveal(f)} onChoose={() => onPick({ ...f, pathLabel: f.path || f.name })} busy={busy} />
          ))}
        </div>
      )}
      {!searching && !here.id && recent.length > 0 && (
        <div className="bd-section">
          <div className="bd-label">Recent</div>
          {recent.map((f) => (
            <FolderRow key={`r-${f.id}`} f={f} sub={f.path || (f.used ? 'Used by your boards' : '')} onOpen={() => reveal(f)} onChoose={() => onPick({ ...f, pathLabel: f.path || f.name })} busy={busy} />
          ))}
        </div>
      )}
      {!searching && (
        <nav className="bd-crumbs" aria-label="Folder path">
          {trail.map((c, i) => (
            <span key={`${c.id}-${i}`}>
              {i > 0 && <span className="bd-sep">/</span>}
              <button type="button" className="bd-link" disabled={i === trail.length - 1} onClick={() => goUp(i)}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>
      )}
      {trailError && !searching && <p className="bd-error bd-small">{trailError}</p>}
      <div className="bd-list" role="list">
        {error && <p className="bd-error">{error}</p>}
        {!rows && !error && <p className="bd-muted">Loading folders…</p>}
        {rows && rows.length === 0 && <p className="bd-muted">{searching ? `No folders match “${q}”.` : 'No subfolders.'}</p>}
        {rows?.map((f) => (
          <FolderRow
            key={f.id}
            f={f}
            sub={searching ? f.location || undefined : undefined}
            onOpen={() => (searching ? reveal(f) : openChild(f))}
            onChoose={() => onPick({ ...f, pathLabel: searching ? [...(f.location ? f.location.split(' › ') : []), f.name].join(' / ') : pathLabel(f.name) })}
            busy={busy}
          />
        ))}
      </div>
      {here.id && !searching && (
        <div className="bd-actions">
          <button type="button" className="bd-btn bd-primary" disabled={busy} onClick={() => onPick({ id: here.id!, name: here.name, can_add: true, pathLabel: pathLabel() })} data-testid="use-folder">
            Use “{here.name}”
          </button>
        </div>
      )}
    </div>
  );
}

function FolderRow({ f, sub, onOpen, onChoose, busy }: { f: DriveFolder; sub?: string; onOpen(): void; onChoose(): void; busy?: boolean }) {
  return (
    <div className="bd-row" role="listitem" data-folder-id={f.id}>
      <button type="button" className="bd-row-main" onClick={onOpen} title={`Open ${f.name}`}>
        <span className="bd-folder" aria-hidden>
          {f.shared_drive ? '⛁' : '📁'}
        </span>
        <span className="bd-row-text">
          <span className="bd-row-name">{f.name}</span>
          {sub ? <span className="bd-muted bd-small">{sub}</span> : null}
        </span>
      </button>
      <button type="button" className="bd-btn bd-small-btn" disabled={busy || !f.can_add} title={f.can_add ? '' : "You can't add files here"} onClick={onChoose}>
        Choose
      </button>
    </div>
  );
}

// ---- create-dialog section ------------------------------------------------------------

/** "Save in Google Drive" field for the New board dialog. */
export function FolderField({ api, value, onChange }: { api: BoardDriveApi; value: (DriveFolder & { pathLabel: string }) | null; onChange(f: (DriveFolder & { pathLabel: string }) | null): void }) {
  const [open, setOpen] = useState(!value);
  return (
    <div className="bd-field" data-testid="folder-field">
      <div className="bd-label">Google Drive folder</div>
      <p className="bd-muted bd-small">
        The board is saved there as a PDF snapshot. Anyone the folder is shared with can open it: editors can edit, commenters and viewers can view.
      </p>
      {value && !open ? (
        <div className="bd-chosen">
          <span>📁 {value.pathLabel || value.name}</span>
          <button type="button" className="bd-link" onClick={() => setOpen(true)}>
            Change
          </button>
        </div>
      ) : (
        <>
          <FolderPicker
            api={api}
            onPick={(f) => {
              onChange(f);
              setOpen(false);
            }}
          />
          <button type="button" className="bd-link bd-small" onClick={() => (onChange(null), setOpen(false))} data-testid="folder-later">
            Decide later (only you can open it until then)
          </button>
        </>
      )}
    </div>
  );
}

// ---- share / access panel -------------------------------------------------------------

export interface SharePanelProps {
  api: BoardDriveApi;
  boardId: string;
  role: BoardRole;
  shareEnabled: boolean;
  /** Absolute join link for a token. */
  joinUrl(token: string): string;
  onChanged?(change: { shareEnabled?: boolean; drive?: BoardDriveStatus }): void;
  onClose(): void;
}

export function BoardSharePanel(p: SharePanelProps) {
  const [drive, setDrive] = useState<BoardDriveStatus | null>(null);
  const [people, setPeople] = useState<BoardAccessRow[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState<{ enabled: boolean; url: string | null }>({ enabled: p.shareEnabled, url: null });
  const [error, setError] = useState<string | null>(null);
  const owner = p.role === 'owner';

  useEffect(() => {
    // The board the caller holds may be stale (link toggled elsewhere): trust the server.
    p.api
      .status(p.boardId)
      .then((d) => {
        setDrive(d);
        setShare({ enabled: d.share_enabled, url: d.share_token ? p.joinUrl(d.share_token) : null });
      })
      .catch((e: Error) => setError(e.message));
    p.api.access(p.boardId).then((r) => setPeople(r.members)).catch(() => setPeople([]));
  }, [p.boardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (f: DriveFolder) => {
    setBusy(true);
    setError(null);
    try {
      const d = await p.api.setFolder(p.boardId, f.id);
      setDrive(d);
      setPicking(false);
      p.onChanged?.({ drive: d });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async (on: boolean) => {
    const before = share;
    setShare((x) => ({ ...x, enabled: on })); // optimistic; reverted on error
    setBusy(true);
    try {
      if (on) {
        const r = await p.api.shareOn(p.boardId);
        setShare({ enabled: true, url: p.joinUrl(r.token) });
      } else {
        await p.api.shareOff(p.boardId);
        setShare({ enabled: false, url: null });
      }
      p.onChanged?.({ shareEnabled: on });
    } catch (e) {
      setShare(before);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      window.prompt('Copy this link', text);
    }
  };

  const others = useMemo(() => (people ?? []).filter((x) => x.via !== 'owner'), [people]);

  return (
    <DriveModal title="Share" onClose={p.onClose} testId="share-panel">
      {error && <p className="bd-error">{error}</p>}
      <section className="bd-section">
        <div className="bd-label">Google Drive</div>
        {!drive ? (
          <p className="bd-muted">Loading…</p>
        ) : drive.folder_id && !picking ? (
          <>
            <p data-testid="drive-folder">
              Saved in <strong>📁 {drive.folder_name || 'a Drive folder'}</strong>
              {drive.folder_url ? (
                <>
                  {' '}
                  ·{' '}
                  <a className="bd-link" href={drive.folder_url} target="_blank" rel="noreferrer">
                    Open folder
                  </a>
                </>
              ) : null}
              {drive.file_url ? (
                <>
                  {' '}
                  ·{' '}
                  <a className="bd-link" href={drive.file_url} target="_blank" rel="noreferrer">
                    Snapshot PDF
                  </a>
                </>
              ) : null}
            </p>
            <p className="bd-muted bd-small">
              Who can open this board follows the folder's sharing in Google Drive: editors can edit, commenters and viewers can view. To change who has access, share the folder (or the snapshot PDF) in Drive.
              {drive.synced_at ? ` Snapshot saved ${new Date(drive.synced_at).toLocaleString()}.` : ' The snapshot is saved a few seconds after changes.'}
            </p>
            {drive.error && <p className="bd-error bd-small">{drive.error}</p>}
            {owner && (
              <button type="button" className="bd-link" onClick={() => setPicking(true)} data-testid="change-folder">
                Move to another folder
              </button>
            )}
          </>
        ) : owner ? (
          <>
            {!drive.folder_id && (
              <p className="bd-warn" data-testid="needs-folder">
                This board isn't in Google Drive yet, so only you can open it. Choose the job folder it belongs in.
              </p>
            )}
            <FolderPicker api={p.api} onPick={(f) => void choose(f)} busy={busy} />
            {picking && (
              <button type="button" className="bd-link bd-small" onClick={() => setPicking(false)}>
                Cancel
              </button>
            )}
          </>
        ) : (
          <p className="bd-muted">This board isn't in Google Drive yet.</p>
        )}
      </section>

      <section className="bd-section">
        <div className="bd-label">People with access</div>
        {!people ? (
          <p className="bd-muted">Loading…</p>
        ) : (
          <ul className="bd-people" data-testid="access-list">
            {(people ?? []).filter((x) => x.via === 'owner').map((x) => (
              <li key={x.user_id}>
                <span>{x.user_name || x.user_email}</span>
                <span className="bd-muted bd-small">Owner</span>
              </li>
            ))}
            {others.map((x) => (
              <li key={x.user_id}>
                <span>{x.user_name || x.user_email}</span>
                <span className="bd-muted bd-small">
                  {roleLabel(x.role)} · {x.via === 'drive' ? 'via Drive' : x.via === 'link' ? 'via link' : 'member'}
                </span>
              </li>
            ))}
            {others.length === 0 && <li className="bd-muted bd-small">No one else has opened it yet. People with folder access appear here once they open the board.</li>}
          </ul>
        )}
      </section>

      {owner && (
        <section className="bd-section" data-testid="share-link">
          {drive ? (
            <label className="bd-toggle">
              <input type="checkbox" checked={share.enabled} disabled={busy} onChange={(e) => void toggleShare(e.target.checked)} data-testid="share-toggle" />
              <span>Share link (view only)</span>
            </label>
          ) : (
            <p className="bd-muted">Share link: loading…</p>
          )}
          <p className="bd-warn bd-small">Anyone signed in with the link can view the board, even without access to the Drive folder. Leave it off unless you need it.</p>
          {share.enabled && share.url && (
            <div className="bd-copy">
              <input className="bd-input" readOnly value={share.url} aria-label="Share link" data-testid="share-url" onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="bd-btn" onClick={() => void copy(share.url!)}>
                Copy
              </button>
            </div>
          )}
        </section>
      )}
    </DriveModal>
  );
}

// ---- dashboard prompt -----------------------------------------------------------------

/** One-time prompt on the dashboard for the owner's boards (or uploaded PDFs) that aren't in Drive. */
export function NeedsFolderBanner({ count, onReview, noun = ['board', 'boards'] }: { count: number; onReview(): void; noun?: [string, string] }) {
  if (!count) return null;
  return (
    <div className="bd-banner" role="status" data-testid="needs-folder-banner">
      <span>
        {count === 1 ? `1 of your ${noun[1]} isn’t` : `${count} of your ${noun[1]} aren’t`} in Google Drive yet, so only you can open {count === 1 ? 'it' : 'them'}. Choose the folder each one belongs in.
      </span>
      <button type="button" className="bd-btn bd-primary" onClick={onReview}>
        Choose folders
      </button>
    </div>
  );
}

/** Walks the owner through choosing a folder for each board without one. */
export function ChooseFoldersDialog({ api, boards, onDone, onClose }: { api: BoardDriveApi; boards: { id: string; title: string }[]; onDone(boardId: string, d: BoardDriveStatus): void; onClose(): void }) {
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const b = boards[i];
  if (!b) return null;
  const next = () => (i + 1 < boards.length ? setI(i + 1) : onClose());
  return (
    <DriveModal title={`Choose a Drive folder · ${i + 1} of ${boards.length}`} onClose={onClose} testId="choose-folders">
      <p>
        <strong>{b.title}</strong>
      </p>
      {error && <p className="bd-error">{error}</p>}
      <FolderPicker
        api={api}
        busy={busy}
        onPick={async (f) => {
          setBusy(true);
          setError(null);
          try {
            onDone(b.id, await api.setFolder(b.id, f.id));
            next();
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      />
      <div className="bd-actions">
        <button type="button" className="bd-btn" onClick={next}>
          Skip this board
        </button>
      </div>
    </DriveModal>
  );
}
